import React, { useEffect, useMemo, useState, useCallback } from 'react';
import Select, { SingleValue, StylesConfig } from 'react-select';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import { cn } from '@/lib/utils';
import { getClientById, getClients } from '@/lib/apiService';
import { showErrorToast } from '@/utils/toast-utils';
import { useDebounce } from '@/hooks/useDebounce';
import type { Client } from '@/types/client';
import { formatDelimitedDisplayLabel, formatDisplayLabel } from '@/utils/display-labels';

interface SearchableClientDropdownProps {
  value?: string;
  onChange: (clientId: string, client: Client) => void;
  error?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  appointmentId?: string | number;
  clientId?: string;
  priorityClientId?: string;
  displayVariant?: 'default' | 'questionnaire';
  /** Fired when the resolved client for the current value changes (including async load by id). */
  onEffectiveClientChange?: (client: Client | null) => void;
}

type ClientOption = {
  value: string;
  label: string;
  email?: string;
  client: Client;
};

const mapClientToOption = (client: Client): ClientOption | null => {
  const clientId = String(client?.id ?? '').trim();
  if (!clientId) return null;

  return {
    value: clientId,
    label: getClientDisplayName(client),
    email: client.email,
    client,
  };
};

const clientsFromResponse = (response: unknown): Client[] => {
  if (Array.isArray(response)) return response as Client[];
  if (!response || typeof response !== 'object') return [];

  const clients = (response as { clients?: unknown }).clients;
  return Array.isArray(clients) ? (clients as Client[]) : [];
};

function getClientDisplayName(client: Client): string {
  const fullName = client.fullName?.trim();
  if (fullName) return fullName;

  const firstName = client.firstName?.trim() ?? '';
  const lastName = client.lastName?.trim() ?? '';
  const fallbackName = `${firstName} ${lastName}`.trim();
  if (fallbackName) return fallbackName;

  return client.email?.trim() || 'Unnamed Client';
}

function getClientConditionSummary(client: Client): string {
  const conditionNames = (client.medicalConditionDetails ?? [])
    .map((condition) => formatDisplayLabel(condition.conditionName))
    .filter((conditionName): conditionName is string => Boolean(conditionName));

  if (conditionNames.length > 0) {
    return Array.from(new Set(conditionNames)).slice(0, 2).join(', ');
  }

  const medicalConditions = typeof client.medicalConditions === 'string' ? client.medicalConditions.trim() : '';
  if (medicalConditions) return formatDelimitedDisplayLabel(medicalConditions);

  return '';
}

function getClientStatusLabel(client: Client): string {
  const rawStatus = String(client.status ?? '').trim().toLowerCase();
  if (!rawStatus) return 'Active';

  return formatDisplayLabel(rawStatus);
}

function isClientSearchResult(client: Client): boolean {
  const rawStatus = String(client.status ?? '').trim().toLowerCase();
  if (!rawStatus) return true;
  return ['active', 'inactive', 'suspended', 'archived'].includes(rawStatus);
}

const SearchableClientDropdown: React.FC<SearchableClientDropdownProps> = ({
  value,
  onChange,
  error,
  placeholder = "Search clients by name, email, or phone...",
  className = "",
  disabled = false,
  appointmentId,
  clientId,
  priorityClientId,
  displayVariant = 'default',
  onEffectiveClientChange,
}) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [selectedClientOverride, setSelectedClientOverride] = useState<Client | null>(null);
  const debouncedSearch = useDebounce(searchInput, 300);
  const selectedValue = value?.toString() ?? '';
  const resolvedPriorityClientId = priorityClientId || selectedValue || undefined;

  // Fetch active clients based on search input.
  useEffect(() => {
    let isMounted = true;

    const fetchClientsData = async () => {
      try {
        setLoading(true);
        setFetchError(null);

        const searchParam = debouncedSearch.trim() || undefined;

        const baseParams = {
          limit: 50,
          page: 1,
          search: searchParam,
          status: 'active',
          appointmentId,
          clientId,
          priorityClientId: resolvedPriorityClientId,
        };

        const fetchedClients = clientsFromResponse(await getClients(baseParams)).filter(isClientSearchResult);

        if (!isMounted) return;
        setClients(fetchedClients);
      } catch (err) {
        console.error('Error fetching clients:', err);
        if (isMounted) {
          setFetchError('Failed to load clients.');
          showErrorToast(err, 'Unable to load clients');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchClientsData();

    return () => {
      isMounted = false;
    };
  }, [debouncedSearch, appointmentId, clientId, resolvedPriorityClientId]);

  const selectedClientFromList = useMemo(
    () => clients.find((client) => String(client.id ?? '') === selectedValue) ?? null,
    [clients, selectedValue]
  );

  useEffect(() => {
    if (!selectedValue) {
      setSelectedClientOverride(null);
      return;
    }

    if (selectedClientFromList) {
      setSelectedClientOverride(selectedClientFromList);
      return;
    }

    let isMounted = true;

    const loadSelectedClient = async () => {
      try {
        const response = await getClientById(selectedValue);
        if (isMounted && response.client) {
          setSelectedClientOverride(response.client);
        } else if (isMounted) {
          setSelectedClientOverride(null);
        }
      } catch (err) {
        console.error('Error loading selected client:', err);
        if (isMounted) {
          setSelectedClientOverride(null);
        }
      }
    };

    loadSelectedClient();

    return () => {
      isMounted = false;
    };
  }, [selectedValue, selectedClientFromList]);

  // Map clients to react-select options
  const options = useMemo(
    () => clients.map(mapClientToOption).filter((option): option is ClientOption => option !== null),
    [clients]
  );

  const selectedOption = useMemo(() => {
    const matchedOption = options.find((opt) => opt.value === selectedValue);
    if (matchedOption) return matchedOption;

    if (!selectedClientOverride) return undefined;
    if (String(selectedClientOverride.id ?? '') !== selectedValue) return undefined;

    return mapClientToOption(selectedClientOverride) ?? undefined;
  }, [options, selectedValue, selectedClientOverride]);

  const isResolvingSelectedClient = Boolean(selectedValue && !selectedOption);

  const effectiveClient = useMemo((): Client | null => {
    if (!selectedValue) return null;
    if (selectedClientFromList) return selectedClientFromList;
    if (selectedClientOverride && String(selectedClientOverride.id ?? '') === selectedValue) {
      return selectedClientOverride;
    }
    return null;
  }, [selectedValue, selectedClientFromList, selectedClientOverride]);

  useEffect(() => {
    if (!onEffectiveClientChange) return;
    if (!selectedValue) {
      onEffectiveClientChange(null);
      return;
    }
    if (effectiveClient) {
      onEffectiveClientChange(effectiveClient);
    }
  }, [effectiveClient, onEffectiveClientChange, selectedValue]);

  const handleChange = (option: SingleValue<ClientOption>) => {
    if (option) {
      // Check if option has client property or if we need to find it
      const clientData =
        option.client ||
        clients.find((c) => c.id?.toString?.() === option.value);
      if (clientData) {
        onChange(option.value, clientData);
      } else {
        console.warn('No client data found for option:', option);
        onChange(option.value, {} as Client);
      }
    } else {
      onChange('', {} as Client);
    }
  };

  const handleInputChange = useCallback((newValue: string) => {
    setSearchInput(newValue);
    return newValue;
  }, []);

  const selectStyles: StylesConfig<ClientOption, false> = useMemo(
    () => ({
      control: (base, state) => ({
        ...base,
        minHeight: displayVariant === 'questionnaire' ? 50 : 48,
        borderRadius: displayVariant === 'questionnaire' ? 8 : base.borderRadius,
        backgroundColor: displayVariant === 'questionnaire' ? '#f9fafb' : base.backgroundColor,
        borderColor: error
          ? '#f87171'
          : state.isFocused
            ? displayVariant === 'questionnaire' ? '#25a8da' : '#9ca3af'
            : displayVariant === 'questionnaire' ? '#bcc4ce' : '#d1d5db',
        boxShadow: 'none',
        cursor: disabled ? 'default' : 'pointer',
        '&:hover': {
          borderColor: error ? '#ef4444' : displayVariant === 'questionnaire' ? '#25a8da' : '#9ca3af',
          backgroundColor: displayVariant === 'questionnaire' ? '#fff' : base.backgroundColor,
        },
      }),
      indicatorSeparator: () => ({
        display: 'none',
      }),
      menu: (base) => ({
        ...base,
        zIndex: 9999,
        pointerEvents: 'auto',
        borderRadius: displayVariant === 'questionnaire' ? 8 : base.borderRadius,
        overflow: 'hidden',
        boxShadow: displayVariant === 'questionnaire' ? '0 2px 7px rgba(16, 24, 40, 0.08)' : base.boxShadow,
        border: displayVariant === 'questionnaire' ? '1px solid #e4e7ec' : base.border,
      }),
      menuPortal: (base) => ({
        ...base,
        zIndex: 99999, // Ensure it sits on top of everything including modals
      }),
      menuList: (base) => ({
        ...base,
        pointerEvents: 'auto',
        maxHeight: '300px',
        paddingTop: displayVariant === 'questionnaire' ? 0 : base.paddingTop,
        paddingBottom: displayVariant === 'questionnaire' ? 0 : base.paddingBottom,
      }),
      option: (base, state) => ({
        ...base,
        pointerEvents: 'auto',
        cursor: 'pointer',
        padding: displayVariant === 'questionnaire' ? 0 : base.padding,
        backgroundColor: displayVariant === 'questionnaire'
          ? state.isSelected
            ? '#e9f5fb'
            : state.isFocused
              ? '#f8fbff'
              : '#fff'
          : state.isSelected
            ? '#3b82f6'
            : state.isFocused
              ? '#e5e7eb'
              : 'white',
        color: displayVariant === 'questionnaire' ? '#344054' : base.color,
        '&:active': {
          backgroundColor: displayVariant === 'questionnaire' ? '#e2f3fb' : '#3b82f6',
        },
      }),
      placeholder: (base) => ({
        ...base,
        color: displayVariant === 'questionnaire' ? '#344054' : base.color,
      }),
      singleValue: (base) => ({
        ...base,
        color: displayVariant === 'questionnaire' ? '#1f2937' : base.color,
      }),
    }),
    [disabled, displayVariant, error]
  );

  // Filter out development-only data attributes that cause react-select warnings
  const selectProps = {
    instanceId: "searchable-client-dropdown",
    options,
    value: selectedOption,
    isClearable: true,
    isDisabled: disabled,
    isLoading: loading || isResolvingSelectedClient,
    placeholder,
    styles: selectStyles,
    onChange: handleChange,
    onInputChange: handleInputChange,
    menuPlacement: "auto" as const,
    menuPosition: "fixed" as const,
    menuPortalTarget: typeof window !== 'undefined' ? document.body : undefined,
    closeMenuOnSelect: true,
    formatOptionLabel: (option: ClientOption) => {
      const statusLabel = getClientStatusLabel(option.client);
      const conditionSummary = getClientConditionSummary(option.client);
      if (displayVariant === 'questionnaire') {
        return (
          <div className="pointer-events-none flex items-start gap-3 px-4 py-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-[#edf4fd]">
              <PersonRoundedIcon sx={{ fontSize: 20, color: '#4b91e3' }} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="break-words text-[0.9375rem] font-medium leading-[1.35] text-[#344054]">
                {option.label}
              </div>
              {conditionSummary ? (
                <div className="break-words text-[0.875rem] font-normal leading-[1.35] text-[#98a2b3]">
                  {conditionSummary}
                </div>
              ) : null}
              <div className="break-words text-[0.8125rem] font-semibold leading-[1.35] text-[#c1c7d0]">
                {option.email || 'No email provided'}
                {statusLabel ? (
                  <>
                    <span className="px-1">•</span>
                    {statusLabel}
                  </>
                ) : null}
              </div>
            </div>
          </div>
        );
      }

      return (
        <div className="flex flex-col pointer-events-none">
          <span className="text-sm font-medium text-gray-900 truncate">
            {option.label}
          </span>
          {option.email && (
            <span className="text-xs font-semibold text-gray-500 truncate mt-0.5">{option.email}</span>
          )}
        </div>
      );
    },
    noOptionsMessage: () => 'No clients found',
    loadingMessage: () => 'Loading clients...',
  };

  return (
    <div className={cn("space-y-2", className)}>
      <Select {...selectProps} />

      {error && (
        <p className="text-sm font-medium text-red-500 flex items-center gap-1">
          <span className="text-xs">⚠</span>
          {error}
        </p>
      )}

      {fetchError && (
        <p className="text-sm font-medium text-amber-600 flex items-center gap-1">
          <span className="text-xs">⚠</span>
          {fetchError}
        </p>
      )}
    </div>
  );
};

export default SearchableClientDropdown;
